import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  useTheme,
  Paper,
  useMediaQuery,
  Alert,
  IconButton,
  Stack,
  Button,
  Switch,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import VaultItemCard from './VaultItemCard.jsx'
import VaultFilters from './VaultFilters'
import vaultLogo from './vaultLogo.png'
import { UserContext } from '../../App.js'
import { useNavigate } from 'react-router-dom'
import API from '../../services/API.js'

const CrewVaultPage = () => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [allItems, setAllItems] = useState([])
  const [filteredItems, setFilteredItems] = useState([])
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { user } = useContext(UserContext)
  const userTeams = user.employee?.teams ?? []
  const navigate = useNavigate()
  const [includeArchived, setIncludeArchived] = useState(false)
  const canManageVault =
    ['Governance', 'Manager'].some((team) => userTeams.includes(team)) ||
    user?.security?.isSiteAdmin

  const loadCrewVaults = () => {
    API.get(`/crewvaultcards?includeArchived=${includeArchived ? 'yes' : 'no'}`)
      .then((res) => {
        if (res.status === 200) {
          setAllItems(res.data)
          setFilteredItems(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadCrewVaults()
  }, [includeArchived])

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Paper
        elevation={0}
        square
        sx={{
          p: 2,
          backgroundColor: 'white',
          color: 'anGrey.main',
          borderBottom: `2px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction="row"
          // alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              component="img"
              src={vaultLogo}
              width={48}
              sx={{ objectFit: 'contain' }}
            />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Crew Vault
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {user?.security?.isSiteAdmin || userTeams.includes('Governance')
                  ? 'Secure documents and notes, restricted by role or team'
                  : 'Only items relevant to your role or team are shown here.'}
              </Typography>
            </Box>
          </Box>
          <Box>
            {canManageVault && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => navigate('add')}
                variant="contained"
                color="primary"
              >
                New
              </Button>
            )}
          </Box>
        </Stack>
        <Box mt={2}>
          <VaultFilters
            vaultItems={allItems}
            setFilteredItems={setFilteredItems}
          />
        </Box>
      </Paper>

      {/* Vault Items */}
      <Box sx={{ p: 2 }}>
        {filteredItems.length === 0 &&
          (allItems.length === 0 ? (
            <Alert
              severity="info"
              action={
                canManageVault && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => navigate('add')}
                  >
                    Add an Item
                  </Button>
                )
              }
            >
              {canManageVault
                ? 'The Crew Vault is empty. Use New to publish reference documents and notes to your crews — each item can be restricted by role or team.'
                : 'Nothing has been published to the Crew Vault yet. Items your organisation shares with your role or team will appear here.'}
            </Alert>
          ) : (
            <Alert severity="info">No items match the current filters.</Alert>
          ))}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 2,
          }}
        >
          {[...filteredItems]
            .sort((a, b) => Number(!!b.unread) - Number(!!a.unread))
            .map((item) => (
              <VaultItemCard key={item._id} item={item} />
            ))}
        </Box>
      </Box>
      {canManageVault && (
        <Box>
          <Stack direction="row" alignItems="center">
            <Switch
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            <Typography>Archived</Typography>
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default CrewVaultPage
